
/**
 * Version control utilities for cache busting and update notifications
 */

// This version should be updated with each deployment
// In a real CI/CD pipeline, this could be automatically updated with git commit hash
export const APP_VERSION = "1.0.0";

// Store the current version in localStorage for comparison
const VERSION_KEY = "app_version";
// Key for storing API tokens that should be preserved across updates
const API_TOKENS_KEY = "api_tokens";

/**
 * Checks if the application has been updated since the last visit
 * @returns {boolean} True if an update is detected
 */
export const checkForUpdates = (): boolean => {
  const storedVersion = localStorage.getItem(VERSION_KEY);
  
  // First time visit or localStorage was cleared
  if (!storedVersion) {
    localStorage.setItem(VERSION_KEY, APP_VERSION);
    return false;
  }
  
  // Compare current version with stored version
  return storedVersion !== APP_VERSION;
};

/**
 * Updates the stored version and clears localStorage except for API tokens
 */
export const handleAppUpdate = (): void => {
  // Preserve API tokens
  const apiTokens = localStorage.getItem(API_TOKENS_KEY);
  
  // Clear localStorage
  localStorage.clear();
  
  // Restore API tokens if they existed
  if (apiTokens) {
    localStorage.setItem(API_TOKENS_KEY, apiTokens);
  }
  
  // Set current version
  localStorage.setItem(VERSION_KEY, APP_VERSION);
};

/**
 * Forces a hard reload of the application
 */
export const reloadApplication = (): void => {
  // Force reload from server, not cache
  // Using location.href assignment to force a full page reload
  window.location.href = window.location.href;
};

/**
 * Adds cache busting parameters to asset URLs
 * @param url The original URL
 * @returns The URL with a version parameter
 */
export const addVersionToUrl = (url: string): string => {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}v=${APP_VERSION}`;
};
